// src/app/utils/brand-helpers.ts
import { 
  Minus, 
  TrendingDown, 
  TrendingUp, 
  Target, 
  Award, 
  BarChart3, 
  Zap, 
  Heart, 
  Lightbulb, 
  Shield, 
  XCircle, 
  Info, 
  Clock,
  type LucideIcon 
} from "lucide-react";
import { FALLBACK_MODEL } from "@/app/constants/brand-constants";
import { PROVIDER_LABEL, type ProviderId } from "@/app/constants/models";

// --- Formatting & UI Helpers ---

export function getVelocityTrendIcon(velocity?: unknown): LucideIcon {
  const v = (velocity ?? "").toString().trim().toLowerCase();
  if (v === "rising") return TrendingUp;
  if (v === "falling") return TrendingDown;
  if (v === "stable") return Minus;
  return Minus;
}

export function displayModel(idOrName: string) {
  const key = idOrName as ProviderId;
  return (PROVIDER_LABEL as any)[key] || idOrName;
}

export function providerLabel(id: string) {
  return (PROVIDER_LABEL as Record<string, string>)[id] ?? id;
}

export function getModelData(safeModelComparison: any[], modelName: string) {
  if (!safeModelComparison.length) return FALLBACK_MODEL;
  return (
    safeModelComparison.find((m) => m.model === modelName) ||
    safeModelComparison[0] ||
    FALLBACK_MODEL
  );
}

// --- Data Logic Helpers ---

export function roundScore(score: number | undefined | null): number {
  if (score === undefined || score === null) return 0;
  return Math.round(score);
}

export function normalizeProviderKey(key: string): string {
  return key.toLowerCase().trim();
}

// --- Icon Mappers ---

export function getAttributeIcon(attributeName: string): LucideIcon {
  switch (attributeName) {
    case "Price": return Target;
    case "Quality": return Award;
    case "Selection": return BarChart3;
    case "Sustainability": return Heart;
    case "Innovation": return Lightbulb;
    case "Safety": // Fallback for Shield context
    case "Trust": return Shield;
    default: return Zap;
  }
}

export function getIntegrityIcon(type: string): LucideIcon {
  if (type === "Incorrect Facts") return XCircle;
  if (type === "Missing Facts") return Info;
  return Clock;
}

export interface CognitionConfigState {
  productQuery: string;
  selectedProductName: string | null;
  selectedModels: string[];
  uploadedFile: File | null;
  productSpecRaw: string;
  isConfigMinimized: boolean;
}

export function safeCognitionConfig(cfg: Partial<CognitionConfigState>): CognitionConfigState {
  return {
    productQuery: cfg?.productQuery || "",
    selectedProductName: cfg?.selectedProductName ?? null,
    selectedModels: Array.isArray(cfg?.selectedModels) ? cfg.selectedModels : [],
    uploadedFile: cfg?.uploadedFile ?? null,
    productSpecRaw: cfg?.productSpecRaw || "",
    isConfigMinimized: !!cfg?.isConfigMinimized,
  };
}

export function isConfigChanged(a: CognitionConfigState, b: CognitionConfigState) {
  if (a.productQuery !== b.productQuery) return true;
  if (a.selectedProductName !== b.selectedProductName) return true;
  if (a.productSpecRaw !== b.productSpecRaw) return true;
  if (a.isConfigMinimized !== b.isConfigMinimized) return true;
  if (a.uploadedFile !== b.uploadedFile) return true;
  if (a.selectedModels.length !== b.selectedModels.length) return true;
  
  // Simple array check
  for (let i = 0; i < a.selectedModels.length; i++) {
    if (a.selectedModels[i] !== b.selectedModels[i]) return true;
  }
  return false;
}